/**
 * Freshness engine for the pantry redesign.
 * Derives a freshness state from an item's expiryDate + location + category.
 * Design source: docs/design-handoff-pantry/gm-kit.jsx + gm-proto.jsx:9-20.
 */
import { InventoryItem } from '../redux/inventorySlice';
import { PantryLocation } from '../utils/pantryLocation';
import { shelfLifeExpiryDate } from './shelfLifeService';
import { normalizeIngredientName } from './groceryListService';

export type FreshnessState = 'urgent' | 'soon' | 'fresh' | 'stable' | 'review';

// The category table that used to live here moved into shelfLifeService, which
// reads the food by name first. Nothing referenced these after the move.
const LEFTOVER_REVIEW_AGE_DAYS = 5;

export const STABLE_CATEGORIES = ['Condiments', 'Spices', 'Packaged Foods', 'Other'];

// "À utiliser": only cookable, worth-saving perishables. Fish lives inside Meat.
// Dairy (milk keeps), condiments and staples are deliberately excluded.
export const COOKABLE_CATEGORIES = ['Meat', 'Plant-Based Protein', 'Vegetables', 'Bread/Grains', 'Fruits'];
const COOK_RANK: Record<string, number> = {
  Meat: 0, 'Plant-Based Protein': 0, Vegetables: 1, 'Bread/Grains': 2, Fruits: 3,
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export function daysUntilExpiry(item: Pick<InventoryItem, 'expiryDate'>, today: Date = new Date()): number | null {
  if (!item.expiryDate) return null;
  const { day, month, year } = item.expiryDate;
  const expiry = new Date(year, month - 1, day);
  return Math.round((expiry.getTime() - startOfDay(today).getTime()) / MS_PER_DAY);
}

export function freshnessOf(item: InventoryItem, today: Date = new Date()): FreshnessState {
  if (item.location === 'freezer') return 'fresh';
  if (item.location === 'pantry') return 'stable';
  const days = daysUntilExpiry(item, today);
  if (days == null) {
    if (item.category === 'Leftovers') {
      const ageDays = (startOfDay(today).getTime() - new Date(item.dateAdded).getTime()) / MS_PER_DAY;
      return ageDays > LEFTOVER_REVIEW_AGE_DAYS ? 'review' : 'soon';
    }
    return STABLE_CATEGORIES.includes(item.category) ? 'stable' : 'fresh';
  }
  if (days < 0) return 'review';
  if (days <= 2) return 'urgent';
  if (days <= 6) return 'soon';
  return 'fresh';
}

/**
 * Expiry estimation when the user adds or moves an item (no date typing, ever).
 *
 * A delegate to shelfLifeService, which knows the food by name. Kept as its own
 * export because the pantry screens call it from three places; the category
 * table above is now only the last resort inside that service.
 */
export function estimateExpiry(
  category: string,
  location: PantryLocation,
  today: Date = new Date(),
  name = '',
  opened?: boolean,
): InventoryItem['expiryDate'] | undefined {
  return shelfLifeExpiryDate(
    { name, category, location, opened },
    today,
    name ? normalizeIngredientName(name) : undefined
  );
}

export function isCookCandidate(item: InventoryItem, today: Date = new Date()): boolean {
  if (!COOKABLE_CATEGORIES.includes(item.category)) return false;
  const state = freshnessOf(item, today);
  if (state !== 'urgent' && state !== 'soon') return false;
  const days = daysUntilExpiry(item, today);
  return days != null && days <= 4;
}

export function sortCookList(items: InventoryItem[], today: Date = new Date()): InventoryItem[] {
  return [...items].sort((a, b) => {
    const da = daysUntilExpiry(a, today) ?? 99;
    const db = daysUntilExpiry(b, today) ?? 99;
    if (da !== db) return da - db;
    return (COOK_RANK[a.category] ?? 9) - (COOK_RANK[b.category] ?? 9);
  });
}

/** Fraction of life left, for thin progress bars. Mirrors gm-kit.jsx lifeFrac. */
export function lifeFrac(days: number | null, state: FreshnessState): number {
  if (state === 'stable') return 1;
  if (state === 'review') return 0.5;
  if (days == null) return 1;
  return Math.max(0.04, Math.min(1, (days + 1) / 16));
}
